/**
 * usePageTitle — BULK-1 WP-Calls (Ernesto, Benjamin): the browser tab names
 * what you are looking at — the deal, company, contact or project — instead
 * of the app's name, so a row of tabs reads "Barham Properties · Alicia Gómez
 * · Door onboarding build" rather than "The Process Lounge" five times.
 *
 * ONE writer of `document.title` for the whole app. Before this the only
 * writer was the running timer (TimeTrackingContext), which took the tab over
 * with its clock and put "The Process Lounge" back when it stopped.
 *
 *   usePageTitle(name)      a record on screen names the tab. Records stack:
 *                           the newest one wins (a contact opened from a
 *                           deal), and closing it hands the tab back to the
 *                           one under it. A null / empty name takes no part.
 *   setTabTakeover(text)    the running timer. While set it wins over every
 *                           record — exactly as the timer does today; null
 *                           when the timer stops hands the tab back.
 *
 * With nothing on screen the tab reads APP_TITLE, as it always has.
 */
import { useEffect, useRef } from 'react';

export const APP_TITLE = 'The Process Lounge';

interface Entry { id: number; title: string }

let seq = 0;
const entries: Entry[] = [];
let takeover: string | null = null;

/** What the tab should read right now. */
export function currentTabTitle(): string {
  if (takeover) return takeover;
  const top = entries[entries.length - 1];
  return top ? top.title : APP_TITLE;
}

function apply(): void {
  if (typeof document === 'undefined') return;
  const next = currentTabTitle();
  if (document.title !== next) document.title = next;
}

/** The running timer's tab ("00:12:04 · Acme — Build"); null gives the tab back. */
export function setTabTakeover(text: string | null): void {
  takeover = text && text.trim() ? text : null;
  apply();
}

/** Names the browser tab after a record while it is on screen (see the header). */
export function usePageTitle(title: string | null | undefined): void {
  const idRef = useRef(0);
  if (!idRef.current) idRef.current = ++seq;
  const clean = (title ?? '').replace(/\s+/g, ' ').trim() || null;

  useEffect(() => {
    const id = idRef.current;
    const i = entries.findIndex((e) => e.id === id);
    if (clean) {
      // A rename keeps the record's place in the stack.
      if (i >= 0) entries[i] = { id, title: clean };
      else entries.push({ id, title: clean });
    } else if (i >= 0) {
      entries.splice(i, 1);
    }
    apply();
  }, [clean]);

  useEffect(() => {
    const id = idRef.current;
    return () => {
      const i = entries.findIndex((e) => e.id === id);
      if (i >= 0) entries.splice(i, 1);
      apply();
    };
  }, []);
}

/** Tests only: forget every record and the takeover. */
export function resetTabTitlesForTest(): void {
  entries.splice(0, entries.length);
  takeover = null;
  apply();
}
